import {
  collection,
  doc,
  runTransaction,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';

import {
  TX_TYPES,
  calculateTier,
  calculateEarnPoints,
  getTierConfig,
  normalizeLoyalty,
  addMonthsToDate,
} from '../../../domain/loyaltyCalculations';
import {
  getLoyaltyConfig,
  findExpiredEarnTxs,
  findBillEarnTxs,
  findBillRedeemTxs,
} from './loyaltyService';

// ============================================================
// HELPERS
// ============================================================

const buildLoyaltyUpdate = (newLoyalty) => ({
  'loyalty.currentPoints': newLoyalty.currentPoints,
  'loyalty.lifetimePoints': newLoyalty.lifetimePoints,
  'loyalty.tier': newLoyalty.tier,
  'loyalty.lastCalculatedAt': serverTimestamp(),
});

export const shouldCheckExpiry = (loyalty) => {
  if (!loyalty?.lastExpiryCheckAt) return true;
  const last = loyalty.lastExpiryCheckAt.toMillis
    ? loyalty.lastExpiryCheckAt.toMillis()
    : new Date(loyalty.lastExpiryCheckAt).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  return Date.now() - last > dayMs;
};

// ============================================================
// EARN
// ============================================================

export const earnPointsForBillTx = (
  tx,
  { customerRef, customerSnap, bill, config, employeeId, businessId },
) => {
  if (!bill?.id) throw new Error('earnPointsForBillTx: bill.id is required');

  const customer = customerSnap?.exists() ? customerSnap.data() : {};
  const loyalty = normalizeLoyalty(customer.loyalty);

  const tierAtTime = loyalty.tier || calculateTier(loyalty.lifetimePoints, config);
  const tierConfig = getTierConfig(tierAtTime, config);
  const points = calculateEarnPoints(bill.totalPrice, tierAtTime, config);

  if (points <= 0) {
    return { pointsEarned: 0, earnTxId: null, oldTier: tierAtTime, newTier: tierAtTime };
  }

  const newCurrent = loyalty.currentPoints + points;
  const newLifetime = loyalty.lifetimePoints + points;
  const newTier = calculateTier(newLifetime, config);

  const earnTxRef = doc(collection(db, 'loyaltyTransactions'));
  const expiresAt = Timestamp.fromDate(
    addMonthsToDate(new Date(), config.expirationMonths),
  );

  tx.set(earnTxRef, {
    customerId: customerRef.id,
    billId: bill.id,
    type: TX_TYPES.EARN,
    points,
    remainingPoints: points,
    balanceAfter: newCurrent,
    description: `Fatura — ${bill.id}`,
    ruleSnapshot: {
      earnRate: config.earnRate,
      tierAtTime,
      multiplier: tierConfig.multiplier,
      basedOn: bill.totalPrice,
    },
    expiresAt,
    reversedBy: null,
    businessId: businessId || null,
    createdAt: serverTimestamp(),
    createdBy: employeeId || 'system',
  });

  tx.update(customerRef, {
    ...buildLoyaltyUpdate({ currentPoints: newCurrent, lifetimePoints: newLifetime, tier: newTier }),
  });

  return { pointsEarned: points, earnTxId: earnTxRef.id, oldTier: tierAtTime, newTier };
};

// ============================================================
// COMBINED BILL SAVE
// ============================================================

export const applyBillSaveToLoyaltyTx = (tx, {
  customerRef,
  customerSnap,
  existingEarnRefs = [],
  existingEarnSnaps = [],
  redeemEarnRefs = [],
  redeemEarnSnaps = [],
  pointsToRedeem = 0,
  billId,
  totalPrice,
  employeeId,
  config,
  businessId,
}) => {
  if (!customerRef || !customerSnap) {
    throw new Error('applyBillSaveToLoyaltyTx: customerRef and customerSnap required');
  }
  if (!billId) throw new Error('applyBillSaveToLoyaltyTx: billId is required');
  if (existingEarnRefs.length !== existingEarnSnaps.length) {
    throw new Error('applyBillSaveToLoyaltyTx: existingEarnRefs and existingEarnSnaps length mismatch');
  }
  if (redeemEarnRefs.length !== redeemEarnSnaps.length) {
    throw new Error('applyBillSaveToLoyaltyTx: redeemEarnRefs and redeemEarnSnaps length mismatch');
  }

  const customer = customerSnap.exists() ? customerSnap.data() : {};
  const loyalty = normalizeLoyalty(customer.loyalty);

  let runningCurrent = loyalty.currentPoints;
  let runningLifetime = loyalty.lifetimePoints;
  let reversedCount = 0;
  let reversedPoints = 0;

  // Phase 1: Reverse existing earns (edit)
  const reverseWrites = [];

  for (let i = 0; i < existingEarnSnaps.length; i += 1) {
    const snap = existingEarnSnaps[i];
    if (!snap || !snap.exists()) continue;
    const earn = snap.data();
    if (earn.reversedBy) continue;

    const stillRemaining = Math.max(0, Number(earn.remainingPoints) || 0);
    const originalPoints = Number(earn.points) || 0;

    runningCurrent = Math.max(0, runningCurrent - stillRemaining);
    runningLifetime = Math.max(0, runningLifetime - originalPoints);
    reversedPoints += stillRemaining;
    reversedCount += 1;

    const reverseTxRef = doc(collection(db, 'loyaltyTransactions'));
    reverseWrites.push({
      setRef: reverseTxRef,
      setData: {
        customerId: customerRef.id,
        billId,
        type: TX_TYPES.REVERSE,
        points: -originalPoints,
        remainingPoints: 0,
        balanceAfter: runningCurrent,
        description: `Adisyon güncelleme — ${billId}`,
        ruleSnapshot: null,
        expiresAt: null,
        reversedBy: null,
        businessId: businessId || null,
        createdAt: serverTimestamp(),
        createdBy: employeeId || 'system',
      },
      updateRef: existingEarnRefs[i],
      updateData: {
        reversedBy: reverseTxRef.id,
        remainingPoints: 0,
      },
    });
  }

  // Phase 2: Redeem (FIFO)
  const requestedRedeem = Math.max(0, Math.floor(Number(pointsToRedeem) || 0));
  let pointsRedeemed = 0;
  let redeemTxId = null;
  let redeemSetOp = null;
  const redeemEarnUpdateOps = [];

  if (requestedRedeem > 0) {
    if (requestedRedeem > runningCurrent) {
      const err = new Error(`Yetersiz puan: mevcut ${runningCurrent}, istenen ${requestedRedeem}`);
      err.code = 'insufficient-points';
      throw err;
    }

    const redeemedFromList = [];
    let remaining = requestedRedeem;

    for (let i = 0; i < redeemEarnSnaps.length && remaining > 0; i += 1) {
      const snap = redeemEarnSnaps[i];
      if (!snap || !snap.exists()) continue;
      const earn = snap.data();
      if (earn.reversedBy) continue;

      const earnRemaining = Math.max(0, Number(earn.remainingPoints) || 0);
      if (earnRemaining === 0) continue;

      const draw = Math.min(remaining, earnRemaining);
      remaining -= draw;

      redeemEarnUpdateOps.push({
        ref: redeemEarnRefs[i],
        data: { remainingPoints: earnRemaining - draw },
      });
      redeemedFromList.push({ earnId: redeemEarnRefs[i].id, amount: draw });
    }

    if (remaining > 0) {
      const err = new Error(`FIFO yetersiz: ${remaining} puan için earn bulunamadı (pre-query eksik).`);
      err.code = 'fifo-insufficient';
      throw err;
    }

    pointsRedeemed = requestedRedeem;
    runningCurrent -= pointsRedeemed;

    const redeemTxRef = doc(collection(db, 'loyaltyTransactions'));
    redeemTxId = redeemTxRef.id;
    const redeemRate = Number(config.redeemRate) || 1;

    redeemSetOp = {
      ref: redeemTxRef,
      data: {
        customerId: customerRef.id,
        billId,
        type: TX_TYPES.REDEEM,
        points: -pointsRedeemed,
        remainingPoints: 0,
        balanceAfter: runningCurrent,
        description: `Puan kullanımı — ${billId}`,
        businessId: businessId || null,
        ruleSnapshot: { redeemRate, discount: pointsRedeemed * redeemRate },
        redeemedFrom: redeemedFromList,
        expiresAt: null,
        reversedBy: null,
        createdAt: serverTimestamp(),
        createdBy: employeeId || 'system',
      },
    };
  }

  const loyaltyDiscount = pointsRedeemed * (Number(config.redeemRate) || 1);

  // Phase 3: Compute new earn
  const tierAtTime = calculateTier(runningLifetime, config);
  const tierConfig = getTierConfig(tierAtTime, config);
  const pointsEarned = calculateEarnPoints(totalPrice, tierAtTime, config);

  let earnTxId = null;
  let earnSetOp = null;

  if (pointsEarned > 0) {
    runningCurrent += pointsEarned;
    runningLifetime += pointsEarned;

    const earnTxRef = doc(collection(db, 'loyaltyTransactions'));
    earnTxId = earnTxRef.id;

    earnSetOp = {
      ref: earnTxRef,
      data: {
        customerId: customerRef.id,
        billId,
        type: TX_TYPES.EARN,
        points: pointsEarned,
        remainingPoints: pointsEarned,
        balanceAfter: runningCurrent,
        description: `Fatura — ${billId}`,
        ruleSnapshot: {
          earnRate: config.earnRate,
          tierAtTime,
          multiplier: tierConfig.multiplier,
          basedOn: Number(totalPrice) || 0,
        },
        expiresAt: Timestamp.fromDate(addMonthsToDate(new Date(), config.expirationMonths)),
        reversedBy: null,
        businessId: businessId || null,
        createdAt: serverTimestamp(),
        createdBy: employeeId || 'system',
      },
    };
  }

  const newTier = calculateTier(runningLifetime, config);

  // Phase 4: Write
  for (const op of reverseWrites) {
    tx.set(op.setRef, op.setData);
    tx.update(op.updateRef, op.updateData);
  }
  for (const op of redeemEarnUpdateOps) {
    tx.update(op.ref, op.data);
  }
  if (redeemSetOp) tx.set(redeemSetOp.ref, redeemSetOp.data);
  if (earnSetOp) tx.set(earnSetOp.ref, earnSetOp.data);

  tx.update(customerRef, {
    ...buildLoyaltyUpdate({
      currentPoints: runningCurrent,
      lifetimePoints: runningLifetime,
      tier: newTier,
    }),
  });

  return {
    pointsEarned, earnTxId,
    pointsRedeemed, redeemTxId, loyaltyDiscount,
    reversedCount, reversedPoints,
    oldTier: loyalty.tier, newTier,
  };
};

// ============================================================
// REDEEM
// ============================================================

export const redeemPointsTx = (
  tx,
  { customerRef, customerSnap, earnSnaps, pointsToUse, billId, employeeId, config, businessId },
) => {
  const customer = customerSnap?.exists() ? customerSnap.data() : {};
  const loyalty = normalizeLoyalty(customer.loyalty);

  const requested = Math.max(0, Math.floor(Number(pointsToUse) || 0));
  if (requested === 0) {
    return { pointsRedeemed: 0, redeemTxId: null, affectedEarns: 0, newTier: loyalty.tier };
  }

  if (requested > loyalty.currentPoints) {
    const err = new Error(`Yetersiz puan: kullanılmak istenen ${requested}, mevcut ${loyalty.currentPoints}`);
    err.code = 'insufficient-points';
    throw err;
  }

  let remaining = requested;
  let affectedCount = 0;

  for (const { ref, snap } of earnSnaps) {
    if (remaining <= 0) break;
    if (!snap.exists()) continue;
    const earn = snap.data();
    const available = Math.max(0, Number(earn.remainingPoints) || 0);
    if (available === 0 || earn.reversedBy) continue;

    const deduct = Math.min(available, remaining);
    tx.update(ref, { remainingPoints: available - deduct });
    remaining -= deduct;
    affectedCount += 1;
  }

  if (remaining > 0) {
    const err = new Error(`FIFO yetersiz: ${remaining} puan için earn bulunamadı. Redeem iptal.`);
    err.code = 'fifo-insufficient';
    throw err;
  }

  const newCurrent = loyalty.currentPoints - requested;
  const redeemTxRef = doc(collection(db, 'loyaltyTransactions'));
  tx.set(redeemTxRef, {
    customerId: customerRef.id,
    billId: billId || null,
    type: TX_TYPES.REDEEM,
    points: -requested,
    remainingPoints: 0,
    balanceAfter: newCurrent,
    description: billId ? `Puan kullanımı — ${billId}` : 'Puan kullanımı',
    ruleSnapshot: { redeemRate: config.redeemRate },
    expiresAt: null,
    reversedBy: null,
    businessId: businessId || null,
    createdAt: serverTimestamp(),
    createdBy: employeeId || 'system',
  });

  const newTier = loyalty.tier;
  tx.update(customerRef, {
    ...buildLoyaltyUpdate({ currentPoints: newCurrent, lifetimePoints: loyalty.lifetimePoints, tier: newTier }),
  });

  return { pointsRedeemed: requested, redeemTxId: redeemTxRef.id, affectedEarns: affectedCount, newTier };
};

// ============================================================
// REVERSE
// ============================================================

export const reverseBillPoints = async (billId, employeeId = 'system', businessId) => {
  if (!billId) throw new Error('reverseBillPoints: billId is required');
  if (!businessId) throw new Error('reverseBillPoints: businessId is required');

  const [earnDocs, redeemDocs] = await Promise.all([
    findBillEarnTxs(billId, businessId),
    findBillRedeemTxs(billId, businessId),
  ]);

  if (earnDocs.length === 0 && redeemDocs.length === 0) {
    return { reversedPoints: 0, txsCreated: 0, newTier: null, redeemReversedPoints: 0, earnReversedPoints: 0 };
  }

  const customerId = (earnDocs[0] || redeemDocs[0]).data.customerId;
  const customerRef = doc(db, 'users', customerId);
  const config = await getLoyaltyConfig(businessId);

  const redeemedEarnRefMap = new Map();
  for (const redeemDoc of redeemDocs) {
    const redeemedFrom = Array.isArray(redeemDoc.data.redeemedFrom) ? redeemDoc.data.redeemedFrom : [];
    for (const entry of redeemedFrom) {
      if (entry && entry.earnId && !redeemedEarnRefMap.has(entry.earnId)) {
        redeemedEarnRefMap.set(entry.earnId, doc(db, 'loyaltyTransactions', entry.earnId));
      }
    }
  }
  const redeemedEarnRefs = Array.from(redeemedEarnRefMap.values());

  return await runTransaction(db, async (tx) => {
    const customerSnap = await tx.get(customerRef);
    const earnSnaps = await Promise.all(earnDocs.map((e) => tx.get(e.ref)));
    const redeemSnaps = await Promise.all(redeemDocs.map((r) => tx.get(r.ref)));
    const redeemedEarnSnaps = await Promise.all(redeemedEarnRefs.map((r) => tx.get(r)));
    const redeemedEarnSnapMap = new Map();
    redeemedEarnRefs.forEach((ref, idx) => {
      redeemedEarnSnapMap.set(ref.id, redeemedEarnSnaps[idx]);
    });

    const customer = customerSnap.exists() ? customerSnap.data() : {};
    const loyalty = normalizeLoyalty(customer.loyalty);

    let currentRunning = loyalty.currentPoints;
    let lifetimeReverse = 0;
    let txsCreated = 0;
    let earnReversedPoints = 0;
    let redeemReversedPoints = 0;

    const writes = [];
    const earnRestoreMap = new Map();

    for (let i = 0; i < redeemSnaps.length; i += 1) {
      const redeemSnap = redeemSnaps[i];
      if (!redeemSnap.exists()) continue;
      const redeem = redeemSnap.data();
      if (redeem.reversedBy) continue;

      const redeemPoints = Math.abs(Number(redeem.points) || 0);
      if (redeemPoints === 0) continue;

      const redeemedFrom = Array.isArray(redeem.redeemedFrom) ? redeem.redeemedFrom : [];
      for (const entry of redeemedFrom) {
        if (!entry?.earnId || !entry?.amount) continue;
        earnRestoreMap.set(entry.earnId, (earnRestoreMap.get(entry.earnId) || 0) + Number(entry.amount));
      }

      currentRunning += redeemPoints;
      redeemReversedPoints += redeemPoints;

      const redeemReverseTxRef = doc(collection(db, 'loyaltyTransactions'));
      writes.push({ type: 'set', ref: redeemReverseTxRef, data: {
        customerId, billId, type: TX_TYPES.REVERSE, points: redeemPoints, remainingPoints: 0,
        balanceAfter: currentRunning, description: `Fatura iptali (puan iadesi) — ${billId}`,
        ruleSnapshot: { reason: 'redeem-reverse', originalRedeemId: redeemDocs[i].id },
        expiresAt: null, reversedBy: null, businessId: businessId || null,
        createdAt: serverTimestamp(), createdBy: employeeId,
      }});
      writes.push({ type: 'update', ref: redeemDocs[i].ref, data: { reversedBy: redeemReverseTxRef.id } });
      txsCreated += 1;
    }

    for (const [earnId, restoreAmount] of earnRestoreMap.entries()) {
      const snap = redeemedEarnSnapMap.get(earnId);
      if (!snap || !snap.exists()) continue;
      const earnData = snap.data();
      if (earnData.reversedBy) continue;
      const currentRemaining = Math.max(0, Number(earnData.remainingPoints) || 0);
      const earnOriginal = Math.max(0, Number(earnData.points) || 0);
      const newRemaining = Math.min(earnOriginal, currentRemaining + restoreAmount);
      writes.push({ type: 'update', ref: redeemedEarnRefMap.get(earnId), data: { remainingPoints: newRemaining } });
    }

    for (let i = 0; i < earnSnaps.length; i += 1) {
      const snap = earnSnaps[i];
      if (!snap.exists()) continue;
      const earn = snap.data();
      if (earn.reversedBy) continue;

      const restoredAmount = earnRestoreMap.get(earnDocs[i].id) || 0;
      const baseRemaining = Math.max(0, Number(earn.remainingPoints) || 0);
      const effectiveRemaining = Math.min(Number(earn.points) || 0, baseRemaining + restoredAmount);

      earnReversedPoints += effectiveRemaining;
      lifetimeReverse += Number(earn.points) || 0;
      currentRunning = Math.max(0, currentRunning - effectiveRemaining);

      const reverseTxRef = doc(collection(db, 'loyaltyTransactions'));
      writes.push({ type: 'set', ref: reverseTxRef, data: {
        customerId, billId, type: TX_TYPES.REVERSE, points: -(Number(earn.points) || 0),
        remainingPoints: 0, balanceAfter: currentRunning,
        description: `Fatura iptali — ${billId}`, ruleSnapshot: null, expiresAt: null,
        reversedBy: null, businessId: businessId || null, createdAt: serverTimestamp(), createdBy: employeeId,
      }});
      writes.push({ type: 'update', ref: earnDocs[i].ref, data: { reversedBy: reverseTxRef.id, remainingPoints: 0 } });
      txsCreated += 1;
    }

    if (txsCreated === 0) {
      return { reversedPoints: 0, txsCreated: 0, newTier: loyalty.tier, redeemReversedPoints: 0, earnReversedPoints: 0 };
    }

    const newLifetime = Math.max(0, loyalty.lifetimePoints - lifetimeReverse);
    const newTier = calculateTier(newLifetime, config);

    for (const w of writes) {
      if (w.type === 'set') tx.set(w.ref, w.data);
      else if (w.type === 'update') tx.update(w.ref, w.data);
    }

    tx.update(customerRef, {
      ...buildLoyaltyUpdate({ currentPoints: currentRunning, lifetimePoints: newLifetime, tier: newTier }),
    });

    return {
      reversedPoints: earnReversedPoints - redeemReversedPoints,
      txsCreated, newTier, redeemReversedPoints, earnReversedPoints,
    };
  });
};

// ============================================================
// EXPIRE
// ============================================================

export const checkAndExpirePoints = async (customerId, businessId) => {
  if (!customerId) throw new Error('checkAndExpirePoints: customerId is required');
  if (!businessId) throw new Error('checkAndExpirePoints: businessId is required');

  const expiredDocs = await findExpiredEarnTxs(customerId, businessId);
  if (expiredDocs.length === 0) {
    const customerRef = doc(db, 'users', customerId);
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(customerRef);
        if (!snap.exists()) return;
        tx.update(customerRef, { 'loyalty.lastExpiryCheckAt': serverTimestamp() });
      });
    } catch (e) {
      console.warn('Could not update lastExpiryCheckAt:', e);
    }
    return { expiredPoints: 0, txsCreated: 0, newTier: null };
  }

  const customerRef = doc(db, 'users', customerId);
  const config = await getLoyaltyConfig(businessId);
  const MAX_PER_TX = 200;
  const toProcess = expiredDocs.slice(0, MAX_PER_TX);

  return await runTransaction(db, async (tx) => {
    const customerSnap = await tx.get(customerRef);
    const snaps = await Promise.all(toProcess.map((e) => tx.get(e.ref)));

    const customer = customerSnap.exists() ? customerSnap.data() : {};
    const loyalty = normalizeLoyalty(customer.loyalty);
    let currentRunning = loyalty.currentPoints;
    let totalExpired = 0;
    let txsCreated = 0;
    const writes = [];

    for (let i = 0; i < snaps.length; i += 1) {
      const snap = snaps[i];
      if (!snap.exists()) continue;
      const earn = snap.data();
      const stillRemaining = Math.max(0, Number(earn.remainingPoints) || 0);
      if (stillRemaining === 0 || earn.reversedBy) continue;

      currentRunning = Math.max(0, currentRunning - stillRemaining);
      totalExpired += stillRemaining;

      const expireTxRef = doc(collection(db, 'loyaltyTransactions'));
      writes.push({ type: 'set', ref: expireTxRef, data: {
        customerId, billId: null, type: TX_TYPES.EXPIRE, points: -stillRemaining,
        remainingPoints: 0, balanceAfter: currentRunning,
        description: `Süre dolumu — earn ${toProcess[i].id}`, ruleSnapshot: null, expiresAt: null,
        reversedBy: null, businessId: businessId || null, createdAt: serverTimestamp(), createdBy: 'system',
      }});
      writes.push({ type: 'update', ref: toProcess[i].ref, data: { remainingPoints: 0 } });
      txsCreated += 1;
    }

    for (const w of writes) {
      if (w.type === 'set') tx.set(w.ref, w.data);
      else tx.update(w.ref, w.data);
    }

    const newTier = calculateTier(loyalty.lifetimePoints, config);
    tx.update(customerRef, {
      ...buildLoyaltyUpdate({ currentPoints: currentRunning, lifetimePoints: loyalty.lifetimePoints, tier: newTier }),
      'loyalty.lastExpiryCheckAt': serverTimestamp(),
    });

    return { expiredPoints: totalExpired, txsCreated, newTier };
  });
};

// ============================================================
// ADJUST
// ============================================================

export const adjustPoints = async (customerId, delta, description, employeeId, businessId) => {
  if (!customerId) throw new Error('adjustPoints: customerId is required');
  if (!businessId) throw new Error('adjustPoints: businessId is required');
  const change = Math.floor(Number(delta) || 0);
  if (change === 0) throw new Error('adjustPoints: delta must be non-zero');

  const customerRef = doc(db, 'users', customerId);
  const config = await getLoyaltyConfig(businessId);

  return await runTransaction(db, async (tx) => {
    const customerSnap = await tx.get(customerRef);
    if (!customerSnap.exists()) throw new Error('Customer not found');

    const loyalty = normalizeLoyalty(customerSnap.data().loyalty);
    const newCurrent = Math.max(0, loyalty.currentPoints + change);
    const newLifetime = Math.max(0, loyalty.lifetimePoints + (change > 0 ? change : 0));
    const newTier = calculateTier(newLifetime, config);

    const adjustTxRef = doc(collection(db, 'loyaltyTransactions'));
    const isEarn = change > 0;
    tx.set(adjustTxRef, {
      customerId, billId: null, type: TX_TYPES.ADJUST, points: change,
      remainingPoints: isEarn ? change : 0, balanceAfter: newCurrent,
      description: description || (isEarn ? 'Manuel puan ekleme' : 'Manuel puan kesinti'),
      ruleSnapshot: null,
      expiresAt: isEarn ? Timestamp.fromDate(addMonthsToDate(new Date(), config.expirationMonths)) : null,
      reversedBy: null, businessId: businessId || null,
      createdAt: serverTimestamp(), createdBy: employeeId || 'system',
    });

    tx.update(customerRef, {
      ...buildLoyaltyUpdate({ currentPoints: newCurrent, lifetimePoints: newLifetime, tier: newTier }),
    });

    return { newBalance: newCurrent, adjustTxId: adjustTxRef.id, newTier };
  });
};
